import { ArrowUpRight, Instagram, MessageCircle, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { SITE } from "@/lib/site";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/f37d1f2f-6174-4432-89e3-ea6d96c68309/images/2fa28612d33516214d929b34b67eb10dc5e2ec4ae45dcb611b95a9a216bd0d02.png";

const PARTNERS = [
  "ISO-Ready Processes",
  "DBS Vetted Teams",
  "PAT · IEE 4th Edition",
  "RIDDOR Aware",
  "COSHH Trained",
  "Public Liability £5m",
];

export default function Hero() {
  const waLink = `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(
    "Hi Nexaverve — I'd like to enquire about your services."
  )}`;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="absolute inset-0 grid-line-divider opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Top status line */}
        <div className="flex items-center justify-between mb-12 sm:mb-20">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-[#FBBF24] nx-pulse" />
            <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.25em] text-neutral-400">
              Now booking · {SITE.location}
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
            <MapPin className="w-3.5 h-3.5" />
            {SITE.domain}
          </div>
        </div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-5xl"
        >
          <p
            data-testid="hero-eyebrow"
            className="tag-label mb-6"
          >
            [ Consultancy / Cleaning / Compliance ]
          </p>
          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-7xl lg:text-[88px] font-black leading-[0.95] tracking-[-0.03em] text-white"
          >
            Standards that <span className="text-[#FBBF24]">scale.</span>
            <br />
            Compliance that <span className="italic font-medium">just works.</span>
          </h1>
          <p
            data-testid="hero-subhead"
            className="mt-8 max-w-2xl text-base sm:text-lg text-neutral-400 leading-relaxed"
          >
            Supporting businesses, schools, property managers and commercial
            environments across Manchester and the North West.
          </p>
          <p
            data-testid="hero-subhead-3"
            className="mt-5 max-w-2xl text-base sm:text-lg text-white font-semibold leading-relaxed"
          >
            One number. One accountable team.
          </p>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-quote"
            className="nx-btn-primary"
          >
            Request a Quote <ArrowUpRight className="w-4 h-4" />
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="nx-btn-secondary"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Us
          </a>
          <a
            href={SITE.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-instagram"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-[#FBBF24] transition-colors"
          >
            <Instagram className="w-4 h-4" /> @{SITE.instagramHandle}
          </a>
        </motion.div>

        {/* Marquee */}
        <div className="mt-20 sm:mt-28 border-y border-white/10 overflow-hidden py-5">
          <div className="flex gap-12 nx-marquee whitespace-nowrap">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <span
                key={i}
                className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-12"
              >
                {p}
                <span className="text-[#FBBF24]/40">/</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
